import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import GoAiguaSelect from "components/generic/goaiguaselect";
import ModalContentLayout from "components/generic/modal-content-layout.component";

import { getAllGisLayers } from "services/requests/v1/gislayer";

import { GisLayerTableType, GoAiguaSelectOption } from "types/generic";

import { useGisLayersTreeContext } from "storage/context/gis-layers-tree-hierarchy-context";
import { gisTreeCreateNewNodeFunctions } from "utils/gis-tree-create-new-node-functions";
import { getFirstHierarchyGroupOnList } from "utils/components/gis-tree-hierarchy-functions";

type GisTreeCreateNewNodeProps = {
    closeModalFn: () => void;
};

type SaveButtonStatus = "enabled" | "disabled";

export type NodeType = "group" | "layer";

const GisTreeCreateNewNode = ({ closeModalFn }: GisTreeCreateNewNodeProps) => {
    const [selectedNodeType, setSelectedNodeType] = useState<NodeType>("group");
    const [newNodeName, setNewNodeName] = useState("");
    const [nameInputIsDisabled, setNameInputIsDisabled] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState<
        GoAiguaSelectOption[]
    >([]);
    const [saveButtonStatus, setsaveButtonStatus] =
        useState<SaveButtonStatus>("disabled");

    const { gisHierarchySelected, setGisHierarchyId, setGisHierarchySelected } =
        useGisLayersTreeContext();

    const [selectedOption, setSelectedOption] = useState<GoAiguaSelectOption>({
        value: "",
        label: "",
    });

    const intl = useIntl();

    const { subscribeContent, unsubscribeContent, setError, contentName } =
        useGisLayersTreeContext();

    const {
        createLayerDisabled,
        handleCreateNewNode,
        handleDropdownSelectionChange,
        handleTextInputChange,
    } = gisTreeCreateNewNodeFunctions({
        dropdownOptions,
        gisHierarchySelected,
        newNodeName,
        selectedNodeType,
        selectedOption,
        contentName,
        setsaveButtonStatus,
        closeModalFn,
        setGisHierarchyId,
        setGisHierarchySelected,
        setNewNodeName,
        setSelectedOption,
        setNameInputIsDisabled,
        subscribeContent,
        unsubscribeContent,
        setError,
    });

    useEffect(() => {
        getAllGisLayers().then((res) => {
            setDropdownOptions(
                res.data.map((layer: GisLayerTableType) => ({
                    label: layer.alias,
                    value: layer.id.toString(),
                }))
            );
        });
    }, []);

    useEffect(() => {
        setSelectedOption({ label: "", value: "" });
        setNewNodeName("");
        setsaveButtonStatus("disabled");
    }, [selectedNodeType]);

    useEffect(() => {
        let status: SaveButtonStatus = "enabled";

        if (selectedNodeType === "group") {
            if (!newNodeName.trim()) status = "disabled";
        } else {
            if (!selectedOption.value) status = "disabled";
        }

        setsaveButtonStatus(status);
    }, [newNodeName]);

    return (
        <ModalContentLayout
            firstButtonTitle={intl.formatMessage({ id: "app.gis.cancel" })}
            secondButtonTitle={intl.formatMessage({ id: "app.users.save" })}
            onFirstButtonClick={closeModalFn}
            onSecondButtonClick={handleCreateNewNode}
            firstButtonStatus="enabled"
            secondButtonStatus={saveButtonStatus}
        >
            <div className="c-gis-tree-create-new-node__container">
                <span className="c-gis-tree-create-new-node__span">
                    {intl.formatMessage({
                        id: "app.gis.hierarchy.select_node_type",
                    })}
                </span>

                <div className="c-gis-tree-create-new-node__radio-selector-container">
                    <input
                        type="radio"
                        name="radio"
                        value=","
                        onChange={() => setSelectedNodeType("group")}
                        checked={selectedNodeType === "group"}
                        className="c-gis-tree-create-new-node__radio-button"
                        id="groupRadioSelector"
                    />
                    <label
                        htmlFor="groupRadioSelector"
                        className="c-gis-tree-create-new-node__radio-label"
                    >
                        {intl.formatMessage({
                            id: "app.gis.group",
                        })}
                    </label>

                    <input
                        type="radio"
                        name="radio"
                        value="."
                        onChange={() => setSelectedNodeType("layer")}
                        disabled={createLayerDisabled()}
                        checked={selectedNodeType === "layer"}
                        className="c-gis-tree-create-new-node__radio-button"
                        id="labelRadioSelector"
                    />

                    <label
                        htmlFor="labelRadioSelector"
                        className="c-gis-tree-create-new-node__radio-label"
                    >
                        {intl.formatMessage({
                            id: "app.gis.layer",
                        })}
                    </label>
                </div>

                {getFirstHierarchyGroupOnList(gisHierarchySelected) && (
                    <>
                        <span className="c-gis-tree-create-new-node__span">
                            {selectedNodeType === "group"
                                ? intl.formatMessage({
                                      id: "app.gis.hierarchy.new_group_within",
                                  })
                                : intl.formatMessage({
                                      id: "app.gis.hierarchy.new_layer_within",
                                  })}
                        </span>

                        <div className="c-gis-tree-create-new-node__parent-group-display">
                            {
                                getFirstHierarchyGroupOnList(
                                    gisHierarchySelected
                                )!.name
                            }
                        </div>
                    </>
                )}

                {selectedNodeType === "group" ? (
                    <>
                        <span className="c-gis-tree-create-new-node__span">
                            {intl.formatMessage({
                                id: "app.gis.hierarchy.insert_name",
                            })}
                        </span>
                        <input
                            value={newNodeName}
                            onChange={({ target }) =>
                                handleTextInputChange(target.value)
                            }
                            placeholder={intl.formatMessage({
                                id: "app.gis.hierarchy.new_name",
                            })}
                            className="c-gis-tree-create-new-node__text-input"
                            disabled={nameInputIsDisabled}
                        />
                    </>
                ) : (
                    <>
                        <span className="c-gis-tree-create-new-node__span">
                            {intl.formatMessage({
                                id: "app.gis.hierarchy.select_layer",
                            })}
                        </span>
                        <GoAiguaSelect
                            placeholder={intl.formatMessage({
                                id: "app.gis.hierarchy.select",
                            })}
                            options={dropdownOptions}
                            selectedOption={selectedOption.value}
                            setSelectedOption={handleDropdownSelectionChange}
                        />
                    </>
                )}
            </div>
        </ModalContentLayout>
    );
};

export default GisTreeCreateNewNode;
